import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  Wrap,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

import companiesData from '../../data/companies';
import educationsData from '../../data/educations';

const listVariant = {
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
  hidden: { opacity: 0 },
};

const itemVariant = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: 12 },
};

function EntryRow({ title, role, skills, period, location, colorMode }) {
  return (
    <Box
      as={motion.li}
      variants={itemVariant}
      listStyleType="none"
      py={{ base: 6, md: 8 }}
      borderTop="1px solid"
      borderColor={`mode.${colorMode}.border`}
    >
      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 4, md: 8 }}
        templateColumns={{ base: '1fr', md: 'minmax(9rem, 14rem) 1fr' }}
      >
        <Stack spacing={1}>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color={`mode.${colorMode}.career.subtext`}
          >
            {period}
          </Text>
          <Text
            fontSize="sm"
            color={`mode.${colorMode}.career.subtext`}
            opacity={0.8}
          >
            {location}
          </Text>
        </Stack>

        <Stack spacing={3} minW={0}>
          <Box>
            <Heading
              as="h3"
              fontSize={{ base: 'xl', md: '2xl' }}
              lineHeight="short"
              color={`mode.${colorMode}.career.text`}
            >
              {title}
            </Heading>
            <Text
              mt={1}
              fontSize={{ base: 'sm', md: 'md' }}
              color={`mode.${colorMode}.career.subtext`}
            >
              {role}
            </Text>
          </Box>

          <Wrap spacing={2}>
            {skills.split(',').map((skill) => (
              <Tag
                key={skill.trim()}
                size="sm"
                px={3}
                py={1}
                borderRadius="sm"
                fontWeight="medium"
                color={`mode.${colorMode}.career.subtext`}
                bgColor={`mode.${colorMode}.career.tagBG`}
              >
                {skill.trim()}
              </Tag>
            ))}
          </Wrap>
        </Stack>
      </SimpleGrid>
    </Box>
  );
}

function ExperienceSection({
  eyebrow,
  heading,
  headingAs,
  entries,
  colorMode,
  sectionLabel,
}) {
  return (
    <Box as="section" aria-labelledby={sectionLabel}>
      <Flex
        display="inline-flex"
        px={3}
        py={1}
        mb={5}
        border="1px solid"
        borderColor={`mode.${colorMode}.accent`}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={`mode.${colorMode}.accent`}
        >
          {eyebrow}
        </Text>
      </Flex>

      <Heading
        id={sectionLabel}
        as={headingAs}
        mb={{ base: 6, md: 6 }}
        fontSize={{ base: '3xl', md: '4xl' }}
        lineHeight="shorter"
        color={`mode.${colorMode}.career.text`}
      >
        {heading}
      </Heading>

      <Box
        as={motion.ul}
        initial="hidden"
        animate="visible"
        variants={listVariant}
        m={0}
        p={0}
        borderBottom="1px solid"
        borderColor={`mode.${colorMode}.border`}
      >
        {entries.map((entry) => (
          <EntryRow
            key={`${entry.title}-${entry.period}`}
            {...entry}
            colorMode={colorMode}
          />
        ))}
      </Box>
    </Box>
  );
}

function Companies({ colorMode }) {
  return (
    <ExperienceSection
      eyebrow="Experience"
      heading="Where I've built."
      headingAs="h1"
      entries={companiesData}
      colorMode={colorMode}
      sectionLabel="experience-heading"
    />
  );
}

function Educations({ colorMode }) {
  return (
    <ExperienceSection
      eyebrow="Education"
      heading="Where I've studied."
      headingAs="h2"
      entries={educationsData}
      colorMode={colorMode}
      sectionLabel="education-heading"
    />
  );
}

function SelfDirectedLearning({ colorMode }) {
  // const topics = [
  //   'Distributed systems',
  //   'Fintech infrastructure',
  //   'Blockchain protocols',
  //   'Product engineering',
  // ];

  return (
    <Box as="section" aria-labelledby="self-directed-learning-heading">
      <Flex
        display="inline-flex"
        px={3}
        py={1}
        mb={5}
        border="1px solid"
        borderColor={`mode.${colorMode}.accent`}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color={`mode.${colorMode}.accent`}
        >
          Beyond the syllabus
        </Text>
      </Flex>

      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        spacing={{ base: 5, md: 10 }}
        templateColumns={{ base: '1fr', md: 'minmax(14rem, 1fr) 1.25fr' }}
        py={{ base: 7, md: 9 }}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor={`mode.${colorMode}.border`}
      >
        <Heading
          id="self-directed-learning-heading"
          as="h2"
          fontSize={{ base: '3xl', md: '4xl' }}
          lineHeight="shorter"
          color={`mode.${colorMode}.career.text`}
        >
          Still learning.
          <br />
          Always building.
        </Heading>

        <Stack spacing={5} justify="center">
          <Text
            fontSize={{ base: 'md', md: 'lg' }}
            lineHeight="tall"
            color={`mode.${colorMode}.career.subtext`}
          >
            Formal education gave me the foundations. Curiosity keeps the rest
            moving through hands-on projects, deep dives and the occasional
            rabbit hole worth following.
          </Text>

          {/* <Wrap spacing={2}>
            {topics.map((topic) => (
              <Tag
                key={topic}
                size="sm"
                px={3}
                py={1}
                borderRadius="sm"
                fontWeight="medium"
                color={`mode.${colorMode}.career.subtext`}
                bgColor={`mode.${colorMode}.career.tagBG`}
              >
                {topic}
              </Tag>
            ))}
          </Wrap> */}
        </Stack>
      </SimpleGrid>
    </Box>
  );
}

export { Companies, Educations, SelfDirectedLearning };
